import React, { useState, useRef, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { KeyRound, Mail, ArrowLeft, CheckCircle, Eye, EyeOff } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function ForgotPasswordPage() {
    const navigate = useNavigate();
    
    const [step, setStep] = useState('email'); // email, code, newPassword, success
    const [email, setEmail] = useState('');
    const [code, setCode] = useState(['', '', '', '', '', '']);
    const [newPassword, setNewPassword] = useState('');
    const [confirmPassword, setConfirmPassword] = useState('');
    const [showPassword, setShowPassword] = useState(false);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');
    const [cooldown, setCooldown] = useState(0);
    
    const inputRefs = useRef([]);
    
    useEffect(() => {
        if (cooldown > 0) {
            const timer = setTimeout(() => setCooldown(cooldown - 1), 1000);
            return () => clearTimeout(timer);
        }
    }, [cooldown]);
    
    const handleEmailSubmit = async (e) => {
        e.preventDefault();
        setLoading(true);
        setError('');
        
        try {
            const response = await fetch(`${API_URL}/api/auth/forgot-password`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ email })
            });
            
            if (!response.ok) {
                const data = await response.json();
                throw new Error(data.detail || 'Failed to send reset code');
            }
            
            setStep('code');
            setCooldown(60);
            
        } catch (err) {
            setError(err.message);
        } finally {
            setLoading(false);
        }
    };
    
    const handleCodeChange = (index, value) => {
        if (value && !/^\d$/.test(value)) return;
        
        const newCode = [...code];
        newCode[index] = value;
        setCode(newCode);
        setError('');
        
        if (value && index < 5) {
            inputRefs.current[index + 1]?.focus();
        }
        
        if (value && index === 5 && newCode.join('').length === 6) {
            setStep('newPassword');
        }
    };
    
    const handleKeyDown = (index, e) => {
        if (e.key === 'Backspace' && !code[index] && index > 0) {
            inputRefs.current[index - 1]?.focus();
        }
    };
    
    const handlePasswordReset = async (e) => {
        e.preventDefault();
        
        if (newPassword !== confirmPassword) {
            setError('Passwords do not match');
            return;
        }
        
        if (newPassword.length < 8) {
            setError('Password must be at least 8 characters');
            return;
        }
        
        setLoading(true);
        setError('');
        
        try {
            const response = await fetch(`${API_URL}/api/auth/reset-password`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    email,
                    code: code.join(''),
                    new_password: newPassword
                })
            });
            
            const data = await response.json();
            
            if (!response.ok) {
                throw new Error(data.detail || 'Failed to reset password');
            }
            
            setStep('success');
            
        } catch (err) {
            setError(err.message);
        } finally {
            setLoading(false);
        }
    };
    
    const handleResendCode = async () => {
        if (cooldown > 0) return;
        
        setLoading(true);
        try {
            await fetch(`${API_URL}/api/auth/forgot-password`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ email })
            });
            setCooldown(60);
            setCode(['', '', '', '', '', '']);
        } catch (err) {
            setError('Failed to resend code');
        } finally {
            setLoading(false);
        }
    };
    
    if (step === 'success') {
        return (
            <div className="min-h-screen bg-background flex items-center justify-center p-4">
                <motion.div
                    initial={{ scale: 0.8, opacity: 0 }}
                    animate={{ scale: 1, opacity: 1 }}
                    className="text-center"
                >
                    <div className="w-20 h-20 rounded-full bg-primary/20 flex items-center justify-center mx-auto mb-6">
                        <CheckCircle className="w-10 h-10 text-primary" />
                    </div>
                    <h2 className="text-2xl font-heading font-semibold text-foreground mb-2">
                        Password Reset!
                    </h2>
                    <p className="text-muted-foreground mb-6">
                        Your password has been successfully reset.
                    </p>
                    <Button onClick={() => navigate('/login')} data-testid="go-to-login-btn">
                        Go to Login
                    </Button>
                </motion.div>
            </div>
        );
    }
    
    return (
        <div className="min-h-screen bg-background flex items-center justify-center p-4">
            <div className="w-full max-w-md">
                <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.5 }}
                >
                    <Card className="border-border">
                        <CardContent className="p-8">
                            {/* Step: Email */}
                            {step === 'email' && (
                                <>
                                    <div className="text-center mb-8">
                                        <div className="w-16 h-16 rounded-full bg-secondary/10 flex items-center justify-center mx-auto mb-4">
                                            <KeyRound className="w-8 h-8 text-secondary" />
                                        </div>
                                        <h1 className="text-2xl font-heading font-semibold text-foreground mb-2">
                                            Forgot Password?
                                        </h1>
                                        <p className="text-muted-foreground text-sm">
                                            Enter your email and we'll send you a reset code.
                                        </p>
                                    </div>
                                    
                                    <form onSubmit={handleEmailSubmit}>
                                        <div className="space-y-4">
                                            <div>
                                                <Label htmlFor="email">Email</Label>
                                                <div className="relative mt-1">
                                                    <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                                                    <Input
                                                        id="email"
                                                        type="email"
                                                        value={email}
                                                        onChange={(e) => setEmail(e.target.value)}
                                                        placeholder="you@example.com"
                                                        className="pl-10"
                                                        required
                                                        data-testid="forgot-email-input"
                                                    />
                                                </div>
                                            </div>
                                            
                                            {error && (
                                                <p className="text-destructive text-sm">{error}</p>
                                            )}
                                            
                                            <Button
                                                type="submit"
                                                className="w-full"
                                                disabled={loading}
                                                data-testid="send-reset-code-btn"
                                            >
                                                {loading ? 'Sending...' : 'Send Reset Code'}
                                            </Button>
                                        </div>
                                    </form>
                                </>
                            )}
                            
                            {/* Step: Code */}
                            {step === 'code' && (
                                <>
                                    <div className="text-center mb-8">
                                        <div className="w-16 h-16 rounded-full bg-primary/10 flex items-center justify-center mx-auto mb-4">
                                            <Mail className="w-8 h-8 text-primary" />
                                        </div>
                                        <h1 className="text-2xl font-heading font-semibold text-foreground mb-2">
                                            Enter Reset Code
                                        </h1>
                                        <p className="text-muted-foreground text-sm">
                                            We sent a code to <span className="text-foreground">{email}</span>
                                        </p>
                                    </div>
                                    
                                    <div className="flex justify-center gap-2 mb-6">
                                        {code.map((digit, index) => (
                                            <Input
                                                key={index}
                                                ref={el => inputRefs.current[index] = el}
                                                type="text"
                                                inputMode="numeric"
                                                maxLength={1}
                                                value={digit}
                                                onChange={(e) => handleCodeChange(index, e.target.value)}
                                                onKeyDown={(e) => handleKeyDown(index, e)}
                                                className="w-12 h-14 text-center text-2xl font-bold"
                                                data-testid={`reset-code-${index}`}
                                            />
                                        ))}
                                    </div>
                                    
                                    {error && (
                                        <p className="text-destructive text-sm text-center mb-4">{error}</p>
                                    )}
                                    
                                    <Button
                                        onClick={() => code.join('').length === 6 && setStep('newPassword')}
                                        disabled={code.join('').length !== 6}
                                        className="w-full mb-4"
                                        data-testid="verify-reset-code-btn"
                                    >
                                        Continue
                                    </Button>
                                    
                                    <div className="text-center">
                                        <Button
                                            variant="ghost"
                                            size="sm"
                                            onClick={handleResendCode}
                                            disabled={cooldown > 0}
                                            className="text-primary"
                                        >
                                            {cooldown > 0 ? `Resend in ${cooldown}s` : 'Resend Code'}
                                        </Button>
                                    </div>
                                </>
                            )}
                            
                            {/* Step: New Password */}
                            {step === 'newPassword' && (
                                <>
                                    <div className="text-center mb-8">
                                        <div className="w-16 h-16 rounded-full bg-primary/10 flex items-center justify-center mx-auto mb-4">
                                            <KeyRound className="w-8 h-8 text-primary" />
                                        </div>
                                        <h1 className="text-2xl font-heading font-semibold text-foreground mb-2">
                                            Create New Password
                                        </h1>
                                        <p className="text-muted-foreground text-sm">
                                            Enter your new password below.
                                        </p>
                                    </div>
                                    
                                    <form onSubmit={handlePasswordReset}>
                                        <div className="space-y-4">
                                            <div>
                                                <Label htmlFor="newPassword">New Password</Label>
                                                <div className="relative mt-1">
                                                    <Input
                                                        id="newPassword"
                                                        type={showPassword ? 'text' : 'password'}
                                                        value={newPassword}
                                                        onChange={(e) => setNewPassword(e.target.value)}
                                                        placeholder="••••••••"
                                                        required
                                                        minLength={8}
                                                        data-testid="new-password-input"
                                                    />
                                                    <button
                                                        type="button"
                                                        onClick={() => setShowPassword(!showPassword)}
                                                        className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                                                    >
                                                        {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                                                    </button>
                                                </div>
                                            </div>
                                            
                                            <div>
                                                <Label htmlFor="confirmPassword">Confirm Password</Label>
                                                <Input
                                                    id="confirmPassword"
                                                    type={showPassword ? 'text' : 'password'}
                                                    value={confirmPassword}
                                                    onChange={(e) => setConfirmPassword(e.target.value)}
                                                    placeholder="••••••••"
                                                    required
                                                    className="mt-1"
                                                    data-testid="confirm-password-input"
                                                />
                                            </div>
                                            
                                            {error && (
                                                <p className="text-destructive text-sm">{error}</p>
                                            )}
                                            
                                            <Button
                                                type="submit"
                                                className="w-full"
                                                disabled={loading}
                                                data-testid="reset-password-btn"
                                            >
                                                {loading ? 'Resetting...' : 'Reset Password'}
                                            </Button>
                                        </div>
                                    </form>
                                </>
                            )}
                            
                            {/* Back to login */}
                            <div className="mt-6 pt-6 border-t border-border text-center">
                                <Link
                                    to="/login"
                                    className="inline-flex items-center text-sm text-muted-foreground hover:text-foreground"
                                >
                                    <ArrowLeft className="w-4 h-4 mr-2" />
                                    Back to Login
                                </Link>
                            </div>
                        </CardContent>
                    </Card>
                </motion.div>
            </div>
        </div>
    );
}
